import ModuleLandingPage from "../../Components/ModuleLandingPage";
import { getNavigationRoutes } from "../../Config/routes";
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";

import TextField from "@material-ui/core/TextField";
import ChatIcon from "@material-ui/icons/Chat";
import NotificationsIcon from "@material-ui/icons/Notifications";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import SortableTable from "../../Components/SortableTable";
import Title from "../../Components/Title";

import { useTranslation } from "react-i18next";

import {
  ECABIN,
  findValueInText,
  formatClientInfoString,
  formatTimeWithTZOffset,
  getToken,
  getUser,
  getWarehouses,
  requestErrorHandler,
  searchMatchingList,
  shipmentStatus,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import EditShipment from "./EditShipment";
import NewShipment from "./NewShipment";

const server = EnvSettings.server;
const DEFAULT_SHIPMENT_FILTER_STATES = ["waiting", "transit"];

const getInitialFilterStates = (shipmentId) =>
  shipmentId
    ? shipmentStatus.map((m) => m.value)
    : DEFAULT_SHIPMENT_FILTER_STATES;

const mergeShipmentsById = (currentShipments, newShipments) => {
  const shipmentsById = new Map();
  [...(currentShipments || []), ...(newShipments || [])].forEach((shipment) => {
    if (shipment?.id) {
      shipmentsById.set(shipment.id, shipment);
    }
  });
  return Array.from(shipmentsById.values());
};

const getFilterDateTime = (date, endOfDay) => {
  if (!date) {
    return undefined;
  }
  const parsedDate = new Date(
    date + (endOfDay ? "T23:59:59.999" : "T00:00:00"),
  );
  const time = parsedDate.getTime();
  return Number.isNaN(time) ? undefined : time;
};

const isDateInRange = (dateValue, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) {
    return true;
  }
  if (!dateValue) {
    return false;
  }
  const dateTime = new Date(dateValue).getTime();
  if (Number.isNaN(dateTime)) {
    return false;
  }
  const fromTime = getFilterDateTime(dateFrom);
  const toTime = getFilterDateTime(dateTo, true);
  return (
    (fromTime === undefined || dateTime >= fromTime) &&
    (toTime === undefined || dateTime <= toTime)
  );
};

const getShipmentDisplayUpdatedAt = (shipment) => {
  if (shipment?.Notes && shipment.Notes.length) {
    const latestNote = shipment.Notes.reduce((prev, current) =>
      prev && prev.id > current.id ? prev : current,
    );
    return latestNote?.updatedAt;
  }
  return shipment?.activityUpdatedAt || shipment?.updatedAt;
};

export default function Shipments(props) {
  const shipment_id = props?.computedMatch?.params?.shipment_id;
  const order_id = props?.computedMatch?.params?.order_id;
  const resupply_order_id = props?.computedMatch?.params?.resupply_order_id;

  const history = useHistory();
  const [searchList, setSearchList] = useState();
  const [shipmentsList, setShipmentsList] = useState();
  const [searchValue, setSearchValue] = useState(() => shipment_id || "");
  const [shipmentFilters, setShipmentFilters] = useState({});
  const [expandShipmentItems, setExpandShipmentItems] = useState(false);
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  const [showForm, setShowForm] = useState(
    () => !!order_id || !!resupply_order_id,
  ); // This opens up the shipment creation when the Add shipment button is pressed from Resupply Order or Order edit view
  const [filterStates, setFilterStates] = useState(() =>
    getInitialFilterStates(shipment_id),
  ); // If a specific shipment is determined on the url, show all the possible states
  const [fetchedFilterStates, setFetchedFilterStates] = useState([]);
  const [loadingFilterStates, setLoadingFilterStates] = useState([]);
  const [selectedShipment, setSelectedShipment] = useState();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [warehouseLocation, setWarehouseLocation] = useState();
  useEffect(() => {
    getWarehouses(setWarehouseLocation, dispatch);
  }, [dispatch]);

  const editBoxRef = useRef(null);

  const updateShipmentFilter = (key, value) => {
    setShipmentFilters((currentFilters) => ({
      ...currentFilters,
      [key]: value || undefined,
    }));
  };

  const executeScroll = () => {
    editBoxRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const getShipmentShippingInfoText = (shipment) => {
    const data = shipment?.shippingInfo;
    const warehouseName = formatClientInfoString(data, ["companyName"]);
    const warehouseLabel =
      warehouseLocation?.find((warehouse) => warehouseName === warehouse.value)
        ?.label || warehouseName;

    if (!shipment?.Order && !shipment?.ResupplyOrder) {
      if (shipment?.shipmentDirection === "outward") {
        return formatClientInfoString(data, [
          "companyName",
          "contactName",
          "location",
        ]);
      }
      if (
        shipment?.shipmentDirection === "inward" ||
        shipment?.shipmentDirection === "between"
      ) {
        return warehouseLabel;
      }
      return "";
    }

    return shipment.Order
      ? formatClientInfoString(data, ["companyName", "contactName", "location"])
      : shipment.ResupplyOrder
        ? warehouseLabel
        : "";
  };

  const showShipment = (dev) => {
    setSelectedShipment();
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        view: "detail",
        shipment_id: dev.id,
      }),
    };
    fetch(server + "/get_shipments", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          // Hack for refreshing the selected device component
          setTimeout(() => {
            setSelectedShipment(result.shipments?.[0] || dev);
            if (editBoxRef && editBoxRef.current) {
              executeScroll();
            }
          }, 1);
        }
      })
      .catch(() => {
        setTimeout(() => {
          setSelectedShipment(dev);
          if (editBoxRef && editBoxRef.current) {
            executeScroll();
          }
        }, 1);
      });
  };

  const getShipments = (statesToFetch, replace) => {
    const shipmentStatesToFetch = Array.isArray(statesToFetch)
      ? statesToFetch.filter(Boolean)
      : filterStates;
    if (!shipmentStatesToFetch.length) {
      if (replace) {
        setShipmentsList([]);
        setSearchList([]);
        setFetchedFilterStates([]);
      }
      return;
    }
    setLoading(true);
    setLoadingFilterStates((currentStates) => [
      ...new Set([...currentStates, ...shipmentStatesToFetch]),
    ]);
    if (replace) {
      setSearchList();
      setSelectedShipment();
    }
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        view: "list",
        shipmentStates: shipmentStatesToFetch,
      }),
    };
    fetch(server + "/get_shipments", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          const fetchedShipments = result.shipments || [];
          setShipmentsList((currentShipments) =>
            replace
              ? fetchedShipments
              : mergeShipmentsById(currentShipments, fetchedShipments),
          );
          setFetchedFilterStates((currentStates) =>
            replace
              ? shipmentStatesToFetch
              : [...new Set([...currentStates, ...shipmentStatesToFetch])],
          );
        }
        setLoading(false);
        setLoadingFilterStates((currentStates) =>
          currentStates.filter(
            (state) => !shipmentStatesToFetch.includes(state),
          ),
        );
      })
      .catch((e) => {
        setLoading(false);
        setLoadingFilterStates((currentStates) =>
          currentStates.filter(
            (state) => !shipmentStatesToFetch.includes(state),
          ),
        );
      });
  };

  const filterResults = () => {
    setSearchList(
      shipmentsList &&
        shipmentsList.filter((contact) => {
          return (
            filterStates.find((o) => o === contact.status) &&
            searchMatchingList(
              [
                contact.number,
                contact.genericDevices,
                contact.Order,
                contact.ResupplyOrder,
                contact.shippingInfo,
                contact.shippingCompany,
                contact,
              ],
              searchValue,
            ) &&
            findValueInText(
              contact.User?.fullName,
              shipmentFilters.createdBy,
            ) &&
            findValueInText(contact.genericDevices, shipmentFilters.items) &&
            findValueInText(
              contact.ShippingCompany?.name || contact.shippingCompany,
              shipmentFilters.shippingCompany,
            ) &&
            (findValueInText(
              getShipmentShippingInfoText(contact),
              shipmentFilters.shippingInfo,
            ) ||
              findValueInText(
                contact.shippingInfo,
                shipmentFilters.shippingInfo,
              )) &&
            findValueInText(contact.externalRef, shipmentFilters.externalRef) &&
            searchMatchingList(
              [contact.Order, contact.ResupplyOrder],
              shipmentFilters.partOfOrder,
            ) &&
            (!shipmentFilters.shipmentDirection ||
              contact.shipmentDirection ===
                shipmentFilters.shipmentDirection) &&
            isDateInRange(
              contact.createdAt,
              shipmentFilters.createdFrom,
              shipmentFilters.createdTo,
            ) &&
            isDateInRange(
              getShipmentDisplayUpdatedAt(contact),
              shipmentFilters.updatedFrom,
              shipmentFilters.updatedTo,
            )
          );
        }),
    );
  };

  useEffect(filterResults, [
    searchValue,
    shipmentsList,
    filterStates,
    shipmentFilters.createdBy,
    shipmentFilters.items,
    shipmentFilters.shippingCompany,
    shipmentFilters.shippingInfo,
    shipmentFilters.externalRef,
    shipmentFilters.partOfOrder,
    shipmentFilters.shipmentDirection,
    shipmentFilters.createdFrom,
    shipmentFilters.createdTo,
    shipmentFilters.updatedFrom,
    shipmentFilters.updatedTo,
    warehouseLocation,
  ]);

  useEffect(() => {
    getShipments(getInitialFilterStates(shipment_id), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   if (shipment_id) {
  //     setFilterStates(shipmentStatus.map((m) => m.value));
  //     setSearchValue(shipment_id);
  //   }
  //   if (order_id) {
  //     setShowForm(true);
  //   }
  //   if (resupply_order_id) {
  //     setShowForm(true);
  //   }
  // }, []);

  const navigationLinks = getNavigationRoutes("shipments").map((route) => ({
    href: route.path,
    title: t(route.title),
    icon: route.icon,
  }));

  return (
    <ModuleLandingPage
      title={t("shipments.header")}
      actions={
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setShowForm(!showForm);
          }}
        >
          {t("shipments.addNewShipment")} +
        </Button>
      }
      navigation={navigationLinks}
      filters={
        <>
          <Grid item xs={12} sm={3}>
            <TextField
              autoFocus
              label={t("search")}
              type="text"
              fullWidth
              variant="outlined"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value.toLowerCase());
              }}
              ref={editBoxRef}
            />
          </Grid>
          <Grid item container spacing={1} xs={12} alignItems="center">
            <Grid item>
              <Title>{t("quickFilters")}</Title>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowAdditionalFilters(!showAdditionalFilters);
                }}
              >
                {showAdditionalFilters ? t("hide") : t("show")}
              </Button>
            </Grid>
          </Grid>
          {showAdditionalFilters ? (
            <>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.createdFrom")}
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={shipmentFilters.createdFrom || ""}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    updateShipmentFilter("createdFrom", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.createdTo")}
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={shipmentFilters.createdTo || ""}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    updateShipmentFilter("createdTo", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.updatedFrom")}
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={shipmentFilters.updatedFrom || ""}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    updateShipmentFilter("updatedFrom", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.updatedTo")}
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={shipmentFilters.updatedTo || ""}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    updateShipmentFilter("updatedTo", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.createdBy")}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={shipmentFilters.createdBy || ""}
                  onChange={(e) => {
                    updateShipmentFilter("createdBy", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.itemsFilter")}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={shipmentFilters.items || ""}
                  onChange={(e) => {
                    updateShipmentFilter("items", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("shipments.shippingCompany")}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={shipmentFilters.shippingCompany || ""}
                  onChange={(e) => {
                    updateShipmentFilter("shippingCompany", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.shippingInfo")}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={shipmentFilters.shippingInfo || ""}
                  onChange={(e) => {
                    updateShipmentFilter("shippingInfo", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("shipments.externalRef")}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={shipmentFilters.externalRef || ""}
                  onChange={(e) => {
                    updateShipmentFilter("externalRef", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("shipments.partOfOrder")}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={shipmentFilters.partOfOrder || ""}
                  onChange={(e) => {
                    updateShipmentFilter("partOfOrder", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("shipments.shipmentDirection")}
                  variant="outlined"
                  select
                  fullWidth
                  value={shipmentFilters.shipmentDirection || ""}
                  onChange={(e) => {
                    updateShipmentFilter("shipmentDirection", e.target.value);
                  }}
                >
                  <MenuItem value="">{t("select")}</MenuItem>
                  {["outward", "inward", "between"].map((direction) => (
                    <MenuItem key={direction} value={direction}>
                      {t("shipments." + direction)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={expandShipmentItems}
                      onChange={(e, checked) => {
                        setExpandShipmentItems(checked);
                      }}
                    />
                  }
                  label={t("orders.expandItems")}
                />
              </Grid>
            </>
          ) : null}
          {searchList ? (
            <Grid container item xs={12}>
              <Grid item xs={11}>
                <Typography>{t("shipments.status")}</Typography>
                {shipmentStatus.map((o, i) => {
                  const isStateLoading = loadingFilterStates.includes(o.value);
                  return (
                    <FormControlLabel
                      key={o.value}
                      control={
                        <Checkbox
                          color={"primary"}
                          disabled={isStateLoading}
                          style={{
                            color: o.color || "",
                          }}
                          onChange={(e, v) => {
                            const nextStates = v
                              ? [...new Set([...filterStates, o.value])]
                              : filterStates.filter((f) => f !== o.value);
                            setFilterStates(nextStates);
                            if (v && !fetchedFilterStates.includes(o.value)) {
                              getShipments([o.value], false);
                            }
                          }}
                          checked={!!filterStates.find((f) => f === o.value)}
                        />
                      }
                      label={
                        <>
                          {t("shipmentStates." + o.value)}
                          {isStateLoading ? (
                            <CircularProgress
                              size={12}
                              style={{ marginLeft: 6 }}
                            />
                          ) : null}
                        </>
                      }
                    />
                  );
                })}
              </Grid>
              <Grid item xs={1}></Grid>
            </Grid>
          ) : null}
        </>
      }
      dialogs={
        <>
          <NewShipment
            open={showForm}
            handleExit={() => {
              setShowForm(false);
              if (order_id || resupply_order_id) {
                history.push("/cpanel/shipments");
                window.location.reload();
              }
            }}
            orderId={order_id}
            resupplyOrderId={resupply_order_id}
          />
          <EditShipment selectedShipment={selectedShipment} />
        </>
      }
    >
      {searchList ? (
        <>
          <SortableTable
            downloadButton={true}
            header={t("shipments.header")}
            tableLayout={"fixed"}
            headCells={[
              {
                id: "Notes",
                label: "",
                width: 32,
                hideFromCsv: true,
              },
              {
                id: "EmailListShipments",
                label: "",
                width: 32,
                hideFromCsv: true,
              },
              {
                id: "updatedAt",
                numeric: false,
                disablePadding: false,
                label: t("updatedAt"),
              },
              {
                id: "createdAt",
                numeric: false,
                disablePadding: false,
                label: t("createdAt"),
              },
              {
                id: "number",
                numeric: false,
                disablePadding: false,
                label: t("shipments.number"),
              },
              {
                id: "externalRef",
                numeric: false,
                disablePadding: false,
                label: t("shipments.externalRef"),
              },
              {
                id: "User.fullName",
                numeric: false,
                disablePadding: false,
                label: t("createdBy"),
              },

              {
                id: "status",
                numeric: false,
                disablePadding: false,
                label: t("shipments.status"),
              },
              {
                id: "eta",
                numeric: false,
                disablePadding: false,
                label: "ETA",
              },
              {
                id: "shippingInfo",
                numeric: false,
                disablePadding: false,
                label: t("orders.shippingInfo"),
              },
              {
                id: "genericDevices",
                numeric: false,
                disablePadding: false,
                label: t("items"),
              },
              {
                id: "Order",
                numeric: false,
                disablePadding: false,
                label: t("shipments.partOfOrder"),
              },
            ]}
            // disablePagination: true,
            defaultSortRow={"updatedAt"}
            handleRowClick={showShipment}
            getRowStyle={(data) => {
              var obj = { cursor: "pointer" };
              var p = shipmentStatus.find((f) => data.status === f.value);
              if (p && p.color) {
                return { ...obj, backgroundColor: p.color };
              }
              return obj;
            }}
            specialDataFormatting={[
              {
                id: "Notes",
                format: (data) => {
                  if (data?.find((f) => f.User)) {
                    return (
                      <Tooltip title={t("activeComments")}>
                        <ChatIcon color="primary" style={{ fontSize: 20 }} />
                      </Tooltip>
                    );
                  }
                  return "";
                },
              },

              {
                id: "EmailListShipments",
                format: (data, d, dd) => {
                  if (
                    dd.isSubscribed ||
                    (data &&
                      data.find(
                        (f) =>
                          f &&
                          f.EmailList?.EmailListUsers.find(
                            (u) => u.User.id === getUser().userId,
                          ),
                      )) ||
                    dd.UserId === getUser().userId
                  ) {
                    return (
                      <Tooltip title={t("tagged")}>
                        <NotificationsIcon
                          color="primary"
                          style={{ fontSize: 20 }}
                        />
                      </Tooltip>
                    );
                  }
                  return "";
                },
              },
              {
                id: "updatedAt",
                format: (data, d, dd) => {
                  if (dd.Notes && dd.Notes.length) {
                    const max = dd.Notes.reduce((prev, current) =>
                      prev && prev.id > current.id ? prev : current,
                    );
                    return formatTimeWithTZOffset(max.updatedAt, ECABIN);
                  }
                  if (dd.activityUpdatedAt) {
                    return formatTimeWithTZOffset(dd.activityUpdatedAt, ECABIN);
                  }
                  return formatTimeWithTZOffset(data, ECABIN);
                },
              },

              {
                id: "createdAt",
                format: (data) => {
                  return formatTimeWithTZOffset(data, ECABIN);
                },
              },
              {
                id: "genericDevices",
                formatReturnsElement: true,
                format: (data, dataFormat) => {
                  const items = Array.isArray(data) ? data : [];
                  const formatItemsForCsv = (shipmentItems) =>
                    shipmentItems
                      .map((v) =>
                        [v.count, "x", v.name, v.sku ? "(" + v.sku + ")" : ""]
                          .filter(Boolean)
                          .join(" "),
                      )
                      .join(" | ");
                  if (dataFormat === "json") {
                    return formatItemsForCsv(items);
                  }
                  const renderItems = (shipmentItems, highlight) => (
                    <>
                      {shipmentItems.map((v, i) => (
                        <Typography
                          key={(v.sku || v.name || "item") + i}
                          style={{
                            ...(highlight ? { backgroundColor: "yellow" } : {}),
                            fontSize: 15,
                          }}
                        >
                          {v.count + " x " + v.name}
                        </Typography>
                      ))}
                    </>
                  );
                  var x;
                  const itemFilterValue = shipmentFilters.items || searchValue;
                  if (itemFilterValue && itemFilterValue !== "") {
                    x = items.filter(
                      (f) =>
                        (f.name || "")
                          .toLowerCase()
                          .includes(itemFilterValue.toLowerCase()) ||
                        (f.sku || "")
                          .toLowerCase()
                          .includes(itemFilterValue.toLowerCase()),
                    );
                    if (x.length) {
                      return renderItems(x, true);
                    }
                  }
                  if (items.length > 1) {
                    if (expandShipmentItems) {
                      return renderItems(items);
                    }
                    x = 0;
                    items.forEach((d) => (x = x + d.count));
                    return t("count") + ":" + x;
                  }
                  var a = "";
                  items.forEach((v) => (a = v.count + " x " + v.name));
                  return a;
                },
              },
              {
                id: "status",
                format: (data) => {
                  return t(
                    "shipmentStates." +
                      shipmentStatus.find((o) => o.value === data).value,
                  );
                },
              },
              {
                id: "shippingInfo",
                format: (data, d, dd) => {
                  return getShipmentShippingInfoText(dd);
                },
              },
              {
                id: "Order",
                format: (data, d, dd) => {
                  if (dd.Order) {
                    return "Order " + dd.Order.number;
                  } else if (dd.ResupplyOrder) {
                    return "Resupply " + dd.ResupplyOrder.number;
                  }
                  return "";
                },
              },
            ]}
            // handleScrollClick: showDeviceNewTab,
            tableData={searchList}
            rowsPerPage={50}
          />
        </>
      ) : loading ? (
        <CircularProgress />
      ) : undefined}
    </ModuleLandingPage>
  );
}
